/**
 * IllustrativeProjectionBanner - efficacy-metric safety surface (PROD-570).
 *
 * Renders the verbatim copy `ILLUSTRATIVE PROJECTION - PRE-PILOT DATA`
 * (HC5 hyphens, never em dashes) followed by the methodology sentence in
 * the Signal DS warning-soft palette. This is the canonical Layer 1
 * safety surface from ADR-001 for efficacy / projection content: every
 * block whose numbers are CDS-literature projections (not measured Scrips
 * outcomes) renders this banner above the metrics so a clinic admin or
 * regulator can never read the numbers as live pilot data.
 *
 * Per ADR-001 Layer 1 + HC4: the banner is persistent (no `dismiss`
 * prop, never auto-dismissed). Hiding the safety surface defeats its
 * purpose; the only way the banner is absent is if its parent metric
 * block is itself absent.
 *
 * Signal DS sourcing per COMPONENT-CONTRACTS §7
 * (IllustrativeProjectionBanner):
 *   - Banner container: Block (Banner variant `warning-soft`)
 *     [GAP: signal-ds-package] - tracked as G7 in
 *     docs/SIGNAL-DS-CONTRIBUTIONS.md until Signal DS publishes the
 *     canonical Banner block primitive.
 *   - Warning palette: Token color (`warning-soft`) - DS-existing via
 *     src/styles/tokens.ts (G6 contributed the warning palette).
 *   - Tooltip variant primitive (for the NBA confidence companion copy
 *     exported as NBA_CONFIDENCE_TOOLTIP_TEXT from
 *     src/constants/nba-tooltip.ts) is also tracked under G7.
 *
 * HC2: every color, spacing, radius value resolves through
 * src/styles/tokens.ts (no raw hex). HC5: zero em dashes anywhere in
 * the rendered copy or in this comment block - the verbatim banner
 * uses HYPHENS.
 *
 * HC6 a11y: the banner is `role="note"` (static informational landmark
 * per WAI-ARIA) with an `aria-label` matching the safety headline so
 * the screen-reader announcement is consistent across every metric
 * block. The leading icon dot is `aria-hidden="true"`. The
 * warning-soft palette meets WCAG 2.1 AA contrast against the surface
 * background per the token map in src/styles/tokens.ts.
 *
 * Verification: tests/illustrative-projection-banner.test.tsx covers
 * IPB-1 through IPB-4 (verbatim copy, palette tokens, every variant
 * renders, role / aria assertions, no em dash in the rendered text).
 * The integration `it.each` test in the same file asserts every Phase
 * 1 view named in the PROD-570 ticket renders the banner above its
 * metric content.
 */

/** Verbatim banner headline - HC5 hyphens, never em dash. */
export const ILLUSTRATIVE_PROJECTION_BANNER_HEADLINE =
  'ILLUSTRATIVE PROJECTION - PRE-PILOT DATA';

/** Verbatim banner body sentence - HC5 hyphens, never em dash. */
export const ILLUSTRATIVE_PROJECTION_BANNER_BODY =
  'Numbers shown are projections based on CDS adoption literature benchmarks, not measured Scrips outcomes. Pilot data will replace these once first clinic deployment is live.';

export interface IllustrativeProjectionBannerProps {
  /**
   * Size variant per COMPONENT-CONTRACTS §7. Defaults to `block-prefix`
   * (full banner above a metric block). `tooltip-attached` is a compact
   * inline form that slots beside an NBA confidence pill - the
   * confidence-tooltip companion copy lives in
   * src/constants/nba-tooltip.ts.
   */
  variant?: 'block-prefix' | 'tooltip-attached';
  className?: string;
}

const VARIANT_CLASSES: Record<
  NonNullable<IllustrativeProjectionBannerProps['variant']>,
  string
> = {
  // Full block-prefix banner: stacked headline + body, comfortable padding.
  'block-prefix':
    'flex flex-col gap-1 px-gutter-md py-gutter-sm text-signal-sm',
  // Compact inline form for tooltip-adjacent surfaces.
  'tooltip-attached':
    'inline-flex items-center gap-gutter-sm px-gutter-sm py-1 text-signal-xs',
};

export function IllustrativeProjectionBanner({
  variant = 'block-prefix',
  className,
}: IllustrativeProjectionBannerProps) {
  // ADR-001 Layer 1: this banner is a persistent safety surface. There is
  // no dismiss, hide, or auto-fade affordance by design.
  const baseClasses =
    'rounded-signal-md bg-warning-soft-bg text-warning-soft-fg ring-1 ring-inset ring-warning-soft-border';
  const composed = [baseClasses, VARIANT_CLASSES[variant], className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <div
      role="note"
      aria-label={ILLUSTRATIVE_PROJECTION_BANNER_HEADLINE}
      data-illustrative-projection-banner={variant}
      className={composed}
    >
      <span className="flex items-center gap-gutter-sm font-bold uppercase tracking-wide">
        <span
          aria-hidden="true"
          className="inline-block h-2 w-2 rounded-signal-pill bg-warning-soft"
        />
        {ILLUSTRATIVE_PROJECTION_BANNER_HEADLINE}
      </span>
      {variant === 'block-prefix' ? (
        <span className="text-signal-sm font-normal normal-case tracking-normal">
          {ILLUSTRATIVE_PROJECTION_BANNER_BODY}
        </span>
      ) : null}
    </div>
  );
}
